import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useLanguage, api } from '../context/AppContext';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, Flame, Shield, Crown, Award, Sparkles } from 'lucide-react';

const GRADE_STYLES = {
  recrue: { color: '#71717A', border: 'border-zinc-600/30' },
  gardien: { color: '#3B82F6', border: 'border-blue-500/30' },
  elite: { color: '#A855F7', border: 'border-purple-500/30' },
  legende: { color: '#FFD700', border: 'border-amber-400/40' },
};

function getTierInfo(legend) {
  const streak = legend.streak || 0;
  const glory = legend.glory_points || 0;
  if (streak >= 8 || glory >= 1000) return { label: 'INFERNO', color: 'text-rose-500', glow: 'shadow-[0_0_30px_rgba(255,0,64,0.15)]' };
  if (streak >= 5 || glory >= 400)  return { label: 'BRASIER', color: 'text-red-500', glow: 'shadow-[0_0_20px_rgba(220,38,38,0.12)]' };
  if (glory >= 100)                 return { label: 'FLAMME', color: 'text-orange-500', glow: '' };
  return { label: 'ETINCELLE', color: 'text-zinc-400', glow: '' };
}

export default function WallOfLegends() {
  const { language, t } = useLanguage();
  const fr = language === 'fr';
  const [legends, setLegends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/workouts/wall-of-legends')
      .then(res => setLegends(res.data.legends || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="wall-of-legends-page">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-[10px] uppercase tracking-[5px] text-muted-foreground mb-2">
            EXTRA MILES FITNESS
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-foreground flex items-center justify-center gap-3">
            <Crown className="w-8 h-8 text-amber-400" />
            {t('pages.wall_legends')}
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            {fr
              ? 'Classement de la Gloire — badges V2, streaks quotidiens et semaines disciplinées combinés. ALL IN ALL TIME.'
              : 'Glory ranking — V2 badges, daily streaks and disciplined weeks combined. ALL IN ALL TIME.'}
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-400/80 bg-amber-500/5 border border-amber-500/20 rounded-full px-3 py-1">
            <Sparkles className="w-3 h-3" />
            {fr ? 'Points connectés au Clash des Meutes' : 'Points connected to Clash of Packs'}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {/* Empty state */}
        {!loading && legends.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Flame className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {fr
                  ? 'Aucune legende pour le moment. Debloque ton premier badge ou log un workout pour apparaitre ici !'
                  : 'No legends yet. Unlock your first badge or log a workout to appear here!'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Legends list */}
        <div className="space-y-3" data-testid="legends-list">
          {legends.map((legend, idx) => {
            const tier = getTierInfo(legend);
            const gradeStyle = GRADE_STYLES[legend.grade_id] || GRADE_STYLES.recrue;

            return (
              <Card
                key={idx}
                className={`${gradeStyle.border} ${tier.glow} overflow-hidden transition-all hover:scale-[1.01]`}
                data-testid={`legend-card-${idx}`}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  {/* Rank */}
                  <div className="w-8 text-center shrink-0">
                    {idx < 3 ? (
                      <span className={`font-heading text-lg font-black ${
                        idx === 0 ? 'text-amber-400' : idx === 1 ? 'text-zinc-300' : 'text-amber-700'
                      }`}>
                        {idx === 0 ? '1' : idx === 1 ? '2' : '3'}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground font-mono">{idx + 1}</span>
                    )}
                  </div>

                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-card border border-border flex items-center justify-center shrink-0 overflow-hidden">
                    {legend.avatar_url ? (
                      <img src={legend.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Shield className="w-5 h-5" style={{ color: gradeStyle.color }} />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-sm font-bold uppercase tracking-tight text-foreground truncate" data-testid={`legend-name-${idx}`}>
                      {legend.warrior_name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: gradeStyle.color }}>
                        {legend.grade_label}
                      </p>
                      {legend.badges_count > 0 && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-amber-400/90"
                          data-testid={`legend-badges-count-${idx}`}
                        >
                          <Award className="w-3 h-3" />
                          {legend.badges_count}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Glory + Streak */}
                  <div className="text-right shrink-0">
                    <div className="flex items-center gap-1.5 justify-end" data-testid={`legend-glory-${idx}`}>
                      <Sparkles className="w-4 h-4 text-amber-400" />
                      <span className="font-heading text-xl font-black text-amber-400">
                        {legend.glory_points || 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 justify-end mt-0.5">
                      <Flame className={`w-3 h-3 ${tier.color}`} />
                      <span className={`text-[11px] font-bold ${tier.color}`}>
                        {legend.streak}j
                      </span>
                      <span className={`text-[9px] uppercase tracking-wider font-bold ${tier.color} ml-1`}>
                        {tier.label}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
